import '@babylonjs/core/Meshes/thinInstanceMesh';
import { gridOf, type Board } from '@archon3d/core';
import { StandardMaterial } from '@babylonjs/core/Materials/standardMaterial';
import { Color3 } from '@babylonjs/core/Maths/math.color';
import { Matrix, Quaternion, Vector3 } from '@babylonjs/core/Maths/math.vector';
import type { Mesh } from '@babylonjs/core/Meshes/mesh';
import { MeshBuilder } from '@babylonjs/core/Meshes/meshBuilder';
import type { TransformNode } from '@babylonjs/core/Meshes/transformNode';
import type { Scene } from '@babylonjs/core/scene';
import { BOARD_TOP_Y, COLORS, TILE_SIZE, WALL_HEIGHT, WALL_THICKNESS } from './constants';
import { cellToWorld } from './layout';

export interface Walls {
  /** Every wall as a thin instance of one slab: one draw call, disabled when the board has none. */
  mesh: Mesh;
  dispose(): void;
}

/** A hex's edge is its circumradius: 1/√3 of its flat-to-flat width, which is one tile. */
const HEX_EDGE = TILE_SIZE / Math.sqrt(3);

/**
 * The walls of a board as stone slabs standing on the edge two neighbouring cells share: a tile
 * long between squares, a hex edge long between hexes, the same height on both grids. Each slab
 * is stretched a thickness past both ends so two that meet at a corner close without a notch.
 * They cast no shadows: the sun is low and thirty slabs would streak the whole board.
 */
export function createWalls(scene: Scene, board: Board, parent: TransformNode | null): Walls {
  const mesh = MeshBuilder.CreateBox('walls', { size: 1 }, scene);
  const walls = board.def.walls ?? [];
  const length = (gridOf(board) === 'hex' ? HEX_EDGE : TILE_SIZE) + WALL_THICKNESS;
  const matrices = new Float32Array(walls.length * 16);
  walls.forEach(([[ax, ay], [bx, by]], i) => {
    const a = cellToWorld(board, ax, ay);
    const b = cellToWorld(board, bx, by);
    // The slab's thickness runs from one cell to the other, so its length lies along the edge.
    const yaw = Math.atan2(b.x - a.x, b.z - a.z);
    Matrix.Compose(
      new Vector3(length, WALL_HEIGHT, WALL_THICKNESS),
      Quaternion.RotationYawPitchRoll(yaw, 0, 0),
      new Vector3((a.x + b.x) / 2, BOARD_TOP_Y + WALL_HEIGHT / 2, (a.z + b.z) / 2),
    ).copyToArray(matrices, i * 16);
  });
  mesh.thinInstanceSetBuffer('matrix', matrices, 16, true);
  mesh.setEnabled(walls.length > 0);

  const material = new StandardMaterial('wallMaterial', scene);
  material.diffuseColor = Color3.FromHexString(COLORS.wall);
  material.specularColor = new Color3(0.12, 0.12, 0.14);
  mesh.material = material;
  mesh.parent = parent;
  mesh.receiveShadows = true;
  mesh.isPickable = false;

  return {
    mesh,
    dispose() {
      mesh.dispose();
      material.dispose();
    },
  };
}
